import * as XLSX from 'xlsx';
import { Project } from '../types';
import { getIntentLabelForProject } from './intentCategories';

/**
 * Exports selected keywords from a project to Excel
 * Only exports keywords that have been selected for each product
 * Includes all optional columns if they have data
 */
export const exportSelectionsToExcel = (
  project: Project,
  selectedKeywords: { [productId: string]: string[] }
) => {
  // Build the data array with headers - include all possible columns
  const data: (string | number | boolean)[][] = [
    [
      'Product Name',
      'Brand',
      'Sub-Brand',
      'Product Category', 
      'Keyword', 
      'Intent Category', 
      'Volume',
      'Ingredients',
      'Legal Claims',
      'Amazon',
      'Target',
      'Walmart',
      'Kroger',
      'Instacart',
      'ds_filter_passed',
      'research_type',
      'intent_score',
      'keyword_eng'
    ],
  ];

  // Iterate through each product
  project.products.forEach((product) => {
    const productSelectedKeywords = selectedKeywords[product.id] || [];

    // Only process products that have selected keywords
    if (productSelectedKeywords.length === 0) {
      return;
    }

    // For each selected keyword, find its details and add a row
    productSelectedKeywords.forEach((keywordText) => {
      // Find the keyword object from the product's keywords array
      const keywordObj = product.keywords.find((k) => k.text === keywordText);

      // Get the intent label for this project
      const intentLabel = keywordObj
        ? getIntentLabelForProject(project, keywordObj.intent)
        : 'Unknown';

      // Add row with all columns (empty string for undefined optional values)
      data.push([
        product.name,
        product.brand || '',
        product.subBrand || '',
        product.category,
        keywordText,
        intentLabel,
        keywordObj?.volume || 0,
        product.ingredients || '',
        product.legalClaims || '',
        keywordObj?.amazon !== undefined ? keywordObj.amazon : '',
        keywordObj?.target !== undefined ? keywordObj.target : '',
        keywordObj?.walmart !== undefined ? keywordObj.walmart : '',
        keywordObj?.kroger !== undefined ? keywordObj.kroger : '',
        keywordObj?.instacart !== undefined ? keywordObj.instacart : '',
        keywordObj?.ds_filter_passed !== undefined ? keywordObj.ds_filter_passed : '',
        keywordObj?.research_type || '',
        keywordObj?.intent_score !== undefined ? keywordObj.intent_score : '',
        keywordObj?.keyword_eng || '',
      ]);
    });
  });

  // Create worksheet and workbook
  const worksheet = XLSX.utils.aoa_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Selected Keywords');

  // Generate Excel file
  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });

  // Create blob and download
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${project.name.replace(/[^a-z0-9]/gi, '_')}_selected_keywords.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

